// KanbanBoardLogic.js
import { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../../contexts/SocketContext'; 
import api, { getLeadsWithRequirements } from '../../services/api'; 
import { useToast } from '@chakra-ui/react';
import moment from 'moment';

const useKanbanBoardLogic = () => {
  const { socket } = useSocket();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [activeMetricFilter, setActiveMetricFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [callFilter, setCallFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [missedFollowUpFilter, setMissedFollowUpFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });
  const [appliedDateFilter, setAppliedDateFilter] = useState({ startDate: null, endDate: null });
  const [focusedInput, setFocusedInput] = useState(null);
  const [datePreset, setDatePreset] = useState('custom');
  const [users, setUsers] = useState([]);
  const [expandedStages, setExpandedStages] = useState({
    'stage_closed_won': true,
    'stage_closed_lost': false, 
    'stage_lead_in': true,
    'stage_nurture': true,
    'stage_proposal_needed': true,
    'stage_fix_meeting': true,
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedLead, setSelectedLead] = useState(null);
  const toast = useToast();

  // Add new state for tracking sequential loading
  const [isLoadingAllPages, setIsLoadingAllPages] = useState(false);

  // Call range state
  const [callRangeMin, setCallRangeMin] = useState(undefined);
  const [callRangeMax, setCallRangeMax] = useState(undefined);

  // Define applyAllFilters early in the component to avoid reference errors
  const applyAllFilters = useCallback((leadsToFilter, customCallRangeMin, customCallRangeMax) => {
    // If no leads, early return to prevent unnecessary processing
    if (!leadsToFilter || leadsToFilter.length === 0) {
      console.log('[applyAllFilters] No leads to filter, returning empty array');
      return [];
    }

    console.log(`[applyAllFilters] Applying filters to ${leadsToFilter.length} leads`, {
      searchQuery,
      callFilter,
      tagFilter,
      assigneeFilter,
      missedFollowUpFilter,
      appliedDateFilter,
      customCallRangeMin,
      customCallRangeMax
    });

    let filtered = [...leadsToFilter]; // Create a copy to avoid mutating the original

    // Apply call filter first as it's often the most restrictive
    if (callFilter) {
      filtered = filtered.filter(lead => {
        // Ensure callCount is treated as a number
        const callCount = Number(lead.callCount || 0);
        
        // Log for debugging purposes
        if (callFilter !== 'any_calls') {
          console.log(`[applyAllFilters] Filtering lead ${lead.leadId} with callCount ${callCount} against filter ${callFilter}`);
        }

        switch (callFilter) {
          case 'no_calls':
            return callCount === 0;
          case 'one_call':
            return callCount === 1;
          case 'two_calls':
            return callCount === 2;
          case 'more_than_two_calls':
            return callCount > 2;
          case 'custom_range': {
            // Custom range filter - get values either from props or URL parameters
            let min, max;
            
            // If customCallRangeMin/Max are undefined, try to get them from URL
            if (customCallRangeMin === undefined || customCallRangeMax === undefined) {
              const urlParams = new URLSearchParams(window.location.search);
              min = Number(urlParams.get('custom_range_min') || '0');
              max = urlParams.get('custom_range_max') ? Number(urlParams.get('custom_range_max')) : Infinity;
            } else {
              min = Number(customCallRangeMin || 0);
              max = customCallRangeMax !== undefined && customCallRangeMax !== '' ? Number(customCallRangeMax) : Infinity;
            }
            
            const result = callCount >= min && callCount <= max;
            console.log(`[applyAllFilters] Custom range filter: lead ${lead.leadId} with callCount ${callCount}, min: ${min}, max: ${max}, result: ${result}`);
            return result;
          }
          case 'any_calls':
          default:
            return true; // No filtering for 'any_calls'
        }
      });

      console.log(`[applyAllFilters] After call filter: ${filtered.length} leads remain`);
    }

    // Apply search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => {
        // Check if lead is defined and has necessary properties
        if (!lead) return false;
        
        return (
          (lead.name && lead.name.toLowerCase().includes(search)) ||
          (lead.brideAndGroomName && lead.brideAndGroomName.toLowerCase().includes(search)) ||
          (lead.email && lead.email.toLowerCase().includes(search)) ||
          (lead.phone && lead.phone.includes(search)) ||
          (lead.source && lead.source.toLowerCase().includes(search))
        );
      });
    }

    // Apply assignee filter
    if (assigneeFilter !== 'all') {
      if (assigneeFilter === 'unassigned') {
        filtered = filtered.filter(lead => !lead.assignedTo);
      } else {
        filtered = filtered.filter(lead => lead.assignedTo === assigneeFilter);
      }
    }

    // Apply tag filter
    if (tagFilter !== 'all') {
      if (tagFilter === 'no_tags') {
        filtered = filtered.filter(lead => !lead.tags || lead.tags.length === 0);
      } else {
        filtered = filtered.filter(lead => lead.tags?.includes(tagFilter));
      }
    }

    // Apply date filter
    if (appliedDateFilter.startDate && appliedDateFilter.endDate) {
      const startDate = appliedDateFilter.startDate.startOf('day').toDate();
      const endDate = appliedDateFilter.endDate.endOf('day').toDate();

      filtered = filtered.filter(lead => {
        if (!lead.dateAdded) return false;
        const leadDate = new Date(lead.dateAdded);
        return leadDate >= startDate && leadDate <= endDate;
      });
    }

    // Apply missed follow-up filter only if no metric filter for follow-ups is active
    if (missedFollowUpFilter !== 'all' && activeMetricFilter !== 'missedFollowUps' && activeMetricFilter !== 'todayFollowUps') {
      const now = new Date();

      if (missedFollowUpFilter === 'missed') {
        filtered = filtered.filter(lead =>
          lead.nextFollowUpTime && new Date(lead.nextFollowUpTime) < now
        );
      } else if (missedFollowUpFilter === 'today') {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        filtered = filtered.filter(lead =>
          lead.nextFollowUpTime &&
          new Date(lead.nextFollowUpTime) >= startOfDay &&
          new Date(lead.nextFollowUpTime) <= endOfDay
        );
      }
    }

    console.log('[KanbanBoardLogic] After filtering:', filtered.length, 'leads');
    return filtered;
  }, [callFilter, tagFilter, assigneeFilter, searchQuery, activeMetricFilter, missedFollowUpFilter, appliedDateFilter]);

  // Function to set callRangeMin and callRangeMax
  const setCallRangeMinMax = (min, max) => {
    console.log(`[KanbanBoardLogic] Setting call range: min=${min}, max=${max}`);
    setCallRangeMin(min);
    setCallRangeMax(max);
    // Reapply filters with the new range values
    const filtered = applyAllFilters(leads, min, max);
    setFilteredLeads(filtered);
  };

  // Function to load a single page of leads
  const fetchLeads = useCallback(async (page = 1, loadSequentially = false) => {
    try {
      setIsLoading(page === 1);
      if (page > 1) setLoadingMore(true);
      
      const response = await getLeadsWithRequirements({ 
        page,
        limit: 50 // Use a reasonable page size
      });
      
      const newLeads = response.data.leads || [];
      const pagination = response.data.pagination || { total: 0, pages: 1, currentPage: 1 };
      
      // Update pagination info
      setCurrentPage(pagination.currentPage);
      setTotalPages(pagination.pages);
      const moreAvailable = pagination.currentPage < pagination.pages;
      setHasMore(moreAvailable);
      
      if (page === 1) {
        // First page - replace all leads
        setLeads(newLeads);
        // Apply all filters to the new leads
        const filtered = applyAllFilters(newLeads, callRangeMin, callRangeMax);
        setFilteredLeads(filtered);
      } else {
        // Additional page - append to existing leads with deduplication
        setLeads(prev => {
          // Create a map of existing leads by ID for quick lookup
          const existingLeadMap = new Map(
            prev.map(lead => [lead._id, lead])
          );
          
          // Only add leads that don't already exist
          const uniqueNewLeads = newLeads.filter(lead => !existingLeadMap.has(lead._id));
          
          // Create combined array with no duplicates
          const combined = [...prev, ...uniqueNewLeads];
          
          // Apply filters only to the newly loaded unique leads
          const newFiltered = applyAllFilters(uniqueNewLeads, callRangeMin, callRangeMax);
          
          // Update filtered leads by appending newly filtered leads
          setFilteredLeads(prevFiltered => {
            // Create a map of existing filtered leads by ID for quick lookup
            const existingFilteredMap = new Map(
              prevFiltered.map(lead => [lead._id, lead])
            );
            
            // Only add filtered leads that don't already exist
            const uniqueNewFiltered = newFiltered.filter(lead => !existingFilteredMap.has(lead._id));
            
            return [...prevFiltered, ...uniqueNewFiltered];
          });
          
          return combined;
        });
      }
      
      // If we're loading sequentially and there are more pages, load the next page
      if (loadSequentially && moreAvailable) {
        // Add a slight delay to avoid overwhelming the server
        setTimeout(() => {
          fetchLeads(pagination.currentPage + 1, true);
        }, 300); 
      } else if (loadSequentially && !moreAvailable) {
        // We've loaded all pages, set the sequential loading state to false
        setIsLoadingAllPages(false);
      }
      
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: 'Error fetching leads',
        description: error.response?.data?.error || 'Something went wrong',
        status: 'error',
        duration: 5000
      });
      // If there's an error during sequential loading, stop the process
      if (loadSequentially) {
        setIsLoadingAllPages(false);
      }
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [toast, applyAllFilters, callRangeMin, callRangeMax]);

  // Add a new function to start loading all pages
  const loadAllLeads = useCallback(() => {
    setIsLoadingAllPages(true);
    // Start from page 1 and set loadSequentially to true
    fetchLeads(1, true);
  }, [fetchLeads]);

  // Load more leads when requested
  const loadMoreLeads = useCallback(() => {
    if (hasMore && !loadingMore && !isLoading) {
      fetchLeads(currentPage + 1);
    }
  }, [fetchLeads, currentPage, hasMore, loadingMore, isLoading]);

  useEffect(() => {
    if (socket) {
      const handleSocketStageChanged = ({ leadId, newStage }) => {
        console.log('[KanbanBoardLogic] Received stage change:', { leadId, newStage });
        setLeads(prev => {
          // Find if the lead exists in our current set
          const leadIndex = prev.findIndex(l => l.leadId === leadId || l._id === leadId);
          
          // If the lead exists, update it
          if (leadIndex !== -1) {
            const updated = [...prev];
            updated[leadIndex] = { ...updated[leadIndex], stage: newStage };
            
            // Apply filters to the updated leads array
            const filteredUpdated = applyAllFilters(updated, callRangeMin, callRangeMax);
            setFilteredLeads(filteredUpdated);
            
            return updated;
          }
          
          // If the lead doesn't exist in our current set (due to pagination),
          // we might need to fetch it - but for now, just return current state
          // to avoid unnecessary API calls
          return prev;
        });
      };

      const handleSocketLeadUpdated = (updatedLead) => {
        console.log('[KanbanBoardLogic] Received lead update:', {
          leadId: updatedLead?.leadId,
          nextFollowUpTime: updatedLead?.nextFollowUpTime,
          callCount: updatedLead?.callCount,
          timeReceived: new Date().toLocaleTimeString()
        });
        
        // Check if this update includes a follow-up time change
        if (updatedLead && updatedLead.nextFollowUpTime) {
          console.log('[KanbanBoardLogic] Socket event includes nextFollowUpTime:', {
            leadId: updatedLead.leadId,
            nextFollowUpTime: updatedLead.nextFollowUpTime,
            formattedTime: new Date(updatedLead.nextFollowUpTime).toLocaleString()
          });
        }
        
        // Create a new lead object with a new reference to force re-render
        const updatedLeadWithNewRef = { 
          ...updatedLead, 
          _updated: Date.now(), // Add a timestamp to ensure reference change
          // Explicitly ensure callCount is a number
          callCount: updatedLead.callCount !== undefined ? Number(updatedLead.callCount) : updatedLead.callCount
        };
        
        console.log('[KanbanBoardLogic] Created new reference for socket updated lead:', {
          leadId: updatedLeadWithNewRef.leadId,
          timestamp: updatedLeadWithNewRef._updated
        });
        
        // First update the main leads array
        setLeads(prev => {
          // Find if the lead exists in our current set
          const leadIndex = prev.findIndex(l => l.leadId === updatedLead.leadId || l._id === updatedLead._id);
          
          // If the lead exists, update it
          if (leadIndex !== -1) {
            // Create a completely new updated array to ensure React detects the change
            const updated = [...prev];
            
            // Track if there's a follow-up time change
            const followUpTimeChanged = 
              updatedLead.nextFollowUpTime !== undefined && 
              prev[leadIndex].nextFollowUpTime !== updatedLead.nextFollowUpTime;
            
            // Keep reference to original lead for logging
            const originalLead = prev[leadIndex];
            
            // Add the properties from the original lead that might not be in the update
            updated[leadIndex] = updatedLeadWithNewRef;
            
            // Log follow-up time changes specifically
            if (followUpTimeChanged) {
              console.log('[KanbanBoardLogic] Updating follow-up time for lead via socket:', {
                leadId: updatedLead.leadId,
                old: originalLead.nextFollowUpTime,
                new: updatedLeadWithNewRef.nextFollowUpTime,
                formattedNew: updatedLeadWithNewRef.nextFollowUpTime ? new Date(updatedLeadWithNewRef.nextFollowUpTime).toLocaleString() : 'none',
                hasReferenceChanged: originalLead !== updatedLeadWithNewRef
              });
            }
            
            console.log('[KanbanBoardLogic] Updated lead in leads array:', {
              leadId: updatedLead.leadId,
              oldCallCount: prev[leadIndex].callCount,
              newCallCount: updated[leadIndex].callCount,
              oldFollowUpTime: prev[leadIndex].nextFollowUpTime, 
              newFollowUpTime: updated[leadIndex].nextFollowUpTime,
              timeUpdated: new Date().toLocaleTimeString(),
              currentFilter: callFilter
            });
            
            // Always reapply filters when there's a lead update to ensure the filtered view is current
            // This is crucial for call filters which depend on the callCount property
            const filteredUpdated = applyAllFilters(updated, callRangeMin, callRangeMax);
            setFilteredLeads(filteredUpdated);
            
            // Also directly update the filtered leads array to ensure the reference change is propagated
            setFilteredLeads(prevFiltered => {
              const filteredIndex = prevFiltered.findIndex(l => 
                l.leadId === updatedLead.leadId || l._id === updatedLead._id
              );
              
              if (filteredIndex !== -1) {
                console.log('[KanbanBoardLogic] Directly updating filtered lead:', {
                  leadId: updatedLead.leadId,
                  index: filteredIndex
                });
                
                const newFiltered = [...prevFiltered];
                newFiltered[filteredIndex] = updatedLeadWithNewRef;
                return newFiltered;
              }
              
              return prevFiltered;
            });
            
            return updated;
          }
          
          // If the lead is new, add it to the beginning of the array
          // First check if this is a genuinely new lead (not a duplicate)
          const isDuplicate = prev.some(l => 
            l.leadId === updatedLead.leadId || 
            l._id === updatedLead._id
          );
          
          if (!isDuplicate) {
            // Ensure callCount is a number
            const leadWithNumberCallCount = {
              ...updatedLead,
              callCount: updatedLead.callCount !== undefined ? Number(updatedLead.callCount) : 0
            };
            
            const updated = [leadWithNumberCallCount, ...prev];
            
            // Apply filters to the updated leads array including the new lead
            const filteredUpdated = applyAllFilters(updated, callRangeMin, callRangeMax);
            setFilteredLeads(filteredUpdated);
            
            return updated;
          }
          
          return prev;
        });
      };

      const handleSocketNewLead = (newLead) => {
        console.log('[KanbanBoardLogic] Received new lead:', newLead);
        
        setLeads(prev => {
          // Check if this lead already exists in our array
          const leadExists = prev.some(l => 
            l.leadId === newLead.leadId || 
            l._id === newLead._id
          );
          
          // Only add the lead if it doesn't already exist
          if (!leadExists) {
            const updated = [newLead, ...prev];
            
            // Apply filters to the updated leads array including the new lead
            const filteredUpdated = applyAllFilters(updated, callRangeMin, callRangeMax);
            setFilteredLeads(filteredUpdated);
            
            return updated;
          }
          
          return prev;
        });
      };
      
      const handleSocketNewComment = ({ leadId, comment }) => {
        console.log('[KanbanBoardLogic] Received new comment for lead:', leadId);
        if (!leads.length) return;
        
        setLeads(prev => {
          // Find if the lead exists in our current set
          const leadIndex = prev.findIndex(l => l.leadId === leadId || l._id === leadId);
          
          // If the lead exists, update its comments
          if (leadIndex !== -1) {
            const updated = [...prev];
            const lead = updated[leadIndex];
            
            // Create a new comments array with the new comment added
            const updatedComments = lead.comments ? [...lead.comments, comment] : [comment];
            updated[leadIndex] = { ...lead, comments: updatedComments };
            
            // Since comments don't affect filtering, no need to re-filter
            // But we need to update filtered leads to show the new comment
            setFilteredLeads(prevFiltered => {
              const filteredLeadIndex = prevFiltered.findIndex(l => 
                l.leadId === leadId || l._id === leadId
              );
              
              if (filteredLeadIndex !== -1) {
                const updatedFiltered = [...prevFiltered];
                updatedFiltered[filteredLeadIndex] = { 
                  ...updatedFiltered[filteredLeadIndex], 
                  comments: updatedComments 
                };
                return updatedFiltered;
              }
              
              return prevFiltered;
            });
            
            return updated;
          }
          
          return prev;
        });
      };
      
      socket.on('stage:changed', handleSocketStageChanged);
      socket.on('lead:updated', handleSocketLeadUpdated);
      socket.on('new:lead', handleSocketNewLead);
      socket.on('new:comment', handleSocketNewComment);

      return () => {
        socket.off('stage:changed', handleSocketStageChanged);
        socket.off('lead:updated', handleSocketLeadUpdated);
        socket.off('new:lead', handleSocketNewLead);
        socket.off('new:comment', handleSocketNewComment);
      };
    }
  }, [socket, applyAllFilters, callRangeMin, callRangeMax]);

  const handleLeadUpdated = async (updatedLead) => {
    console.log('[KanbanBoardLogic] Handling lead update:', {
      leadId: updatedLead?.leadId,
      nextFollowUpTime: updatedLead?.nextFollowUpTime,
      callCount: updatedLead?.callCount,
      timeReceived: new Date().toLocaleTimeString()
    });
    
    if (!updatedLead) {
      console.log('[KanbanBoardLogic] Lead was deleted, refreshing all leads');
      await fetchLeads();
      return;
    }
    
    // If this update includes nextFollowUpTime, explicitly log it
    if (updatedLead.nextFollowUpTime !== undefined) {
      console.log('[KanbanBoardLogic] Updating lead with new follow-up time:', {
        leadId: updatedLead.leadId,
        nextFollowUpTime: updatedLead.nextFollowUpTime,
        formattedTime: new Date(updatedLead.nextFollowUpTime).toLocaleString()
      });
    }

    // Create a new lead object with a new reference to force re-render
    const updatedLeadWithNewRef = { 
      ...updatedLead, 
      _updated: Date.now() // Add a timestamp to ensure reference change
    };
    
    console.log('[KanbanBoardLogic] Created new reference for updated lead:', {
      leadId: updatedLeadWithNewRef.leadId,
      timestamp: updatedLeadWithNewRef._updated
    });

    setLeads(prev => {
      const newLeads = prev.map(lead => {
        if (lead.leadId === updatedLead.leadId) {
          // Log the change for this specific lead
          console.log('[KanbanBoardLogic] Replacing lead in array:', {
            leadId: lead.leadId,
            oldFollowUpTime: lead.nextFollowUpTime,
            newFollowUpTime: updatedLeadWithNewRef.nextFollowUpTime,
            hasReferenceChanged: lead !== updatedLeadWithNewRef
          });
          return updatedLeadWithNewRef;
        }
        return lead;
      });
      
      return newLeads;
    });

    setFilteredLeads(prev => {
      const newFiltered = prev.map(lead => {
        if (lead.leadId === updatedLead.leadId) {
          return updatedLeadWithNewRef;
        }
        return lead;
      });
      
      return newFiltered;
    });

    socket?.emit('lead:update', updatedLead);

    toast({
      title: 'Lead Updated',
      description: 'Lead has been updated successfully',
      status: 'success',
      duration: 3000,
      isClosable: true
    });
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    // Don't emit any socket event here - editing is local to this client
  };

  const handleAdvancedEditLead = (lead) => {
    setSelectedLead(lead);
    // Don't emit any socket event here - editing is local to this client
  };

  const handleAddNewLead = () => {
    setSelectedLead(null);
  };

  const handleModalClose = () => {
    setSelectedLead(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, source, destination } = result;
    const lead = leads.find(l => l.leadId === draggableId);
    const newStageId = destination.droppableId;
    const oldStageId = source.droppableId;

    setLeads(prev => prev.map(l =>
      l.leadId === draggableId ? { ...l, stage: newStageId } : l
    ));

    try {
      socket?.emit('stage:change', {
        leadId: draggableId,
        newStage: newStageId
      });

      const response = await api.put(`/api/leads/${lead.leadId}`, {
        stage: newStageId
      });

      await fetchLeads();

      toast({
        title: 'Success',
        description: 'Lead stage updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error updating lead stage:', error);

      setLeads(prev => prev.map(l =>
        l.leadId === draggableId ? { ...l, stage: oldStageId } : l
      ));

      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || error.message;
        const errorParts = errorMessage.split('\n');
        
        let description = 'Cannot move to next stage';
        if (errorParts.length > 1) {
          // Skip the first line which is just "Cannot move to next stage: Stage requirements not met"
          const requirements = errorParts.slice(1).join('\n');
          description = `${description}. Missing required fields:\n\n${requirements}`;
        }

        toast({
          title: 'Cannot Update Stage',
          description: description,
          status: 'error',
          duration: 10000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.error || 'Failed to update lead stage',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    }
  };

  const fetchStages = async () => {
    try {
      const response = await api.get('/api/constants/STAGES');
      const stageOrder = [
        'stage_lead_in',
        'stage_nurture',
        'stage_proposal_needed',
        'stage_fix_meeting',
        'stage_closed_won',
        'stage_closed_lost'
      ];

      const orderedStages = response.data.values.sort((a, b) => {
        const indexA = stageOrder.indexOf(a._id);
        const indexB = stageOrder.indexOf(b._id);
        return indexA - indexB;
      });
      setStages(orderedStages);
    } catch (error) {
      console.error('Error fetching stages:', error);
      toast({
        title: 'Error fetching stages',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000
      });
    }
  };

  const filterByMetric = (metricType) => {
    setActiveMetricFilter(metricType);
    const filtered = applyAllFilters(leads, callRangeMin, callRangeMax);
    setFilteredLeads(filtered);
  };

  useEffect(() => {
    const filtered = applyAllFilters(leads, callRangeMin, callRangeMax);
    setFilteredLeads(filtered);
  }, [applyAllFilters, leads, callRangeMin, callRangeMax]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: 'Error fetching users',
          description: error.response?.data?.message || error.message,
          status: 'error',
          duration: 5000,
        });
      }
    };

    const initialize = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchStages(), fetchUsers()]);
        // Start loading all leads
        loadAllLeads();
      } finally {
        setIsLoading(false);
      }
    };
    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    leads,
    filteredLeads,
    isLoading,
    hasMore,
    loadingMore,
    isLoadingAllPages,
    stages,
    expandedStages,
    setExpandedStages,
    searchQuery,
    setSearchQuery,
    callFilter,
    setCallFilter,
    tagFilter,
    setTagFilter,
    assigneeFilter,
    setAssigneeFilter,
    missedFollowUpFilter,
    setMissedFollowUpFilter,
    appliedDateFilter,
    setAppliedDateFilter,
    dateFilter,
    setDateFilter,
    datePreset,
    setDatePreset,
    focusedInput,
    setFocusedInput,
    users,
    handleDragEnd,
    handleLeadUpdated,
    handleEditLead,
    handleAdvancedEditLead,
    handleAddNewLead,
    handleModalClose,
    selectedLead,
    fetchLeads,
    filterByMetric,
    loadMoreLeads,
    loadAllLeads,
    setCallRangeMinMax
  };
};

export default useKanbanBoardLogic;